import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Visibility as ShowIcon
} from '@mui/icons-material';
import { invoiceService } from '../../services/invoice.service';
import { INVOICE_API_CONFIG } from '../../services/invoice.service';

/**
 * Invoice API Health Check Component
 * Tests connectivity to the Invoice Processing API
 */
const InvoiceApiHealthCheck = () => {
  const [healthStatus, setHealthStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const checkApiHealth = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await invoiceService.checkHealth();
      setHealthStatus({
        status: 'healthy',
        message: 'Invoice Processing API is running',
        details: response
      });
    } catch (err) {
      setError(err.message);
      setHealthStatus({
        status: 'unhealthy',
        message: 'Invoice Processing API is not accessible',
        details: null
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkApiHealth();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'unhealthy':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <SuccessIcon color="success" />;
      case 'unhealthy':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  if (!isVisible) {
    return (
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ShowIcon />}
          onClick={() => setIsVisible(true)}
        >
          Show API Status
        </Button>
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Invoice Processing API Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={checkApiHealth}
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Refresh'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon />}
              onClick={() => setIsVisible(false)}
            >
              Hide
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            API Configuration:
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Base URL:</strong> {INVOICE_API_CONFIG.baseURL}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>API Key:</strong> {INVOICE_API_CONFIG.apiKey ? 'Configured' : 'Not Set'}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Timeout:</strong> {INVOICE_API_CONFIG.timeout}ms
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Checking API health...</Typography>
          </Box>
        )}

        {healthStatus && !loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(healthStatus.status)}
            <Chip
              label={healthStatus.status.toUpperCase()}
              color={getStatusColor(healthStatus.status)}
              size="small"
            />
            <Typography variant="body2">
              {healthStatus.message}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>Error:</strong> {error}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Make sure the Invoice Processing API server is running on port 8001.
            </Typography>
          </Alert>
        )}

        {healthStatus?.details && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              API Response:
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200
              }}
            >
              {JSON.stringify(healthStatus.details, null, 2)}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default InvoiceApiHealthCheck;
